import { useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useEscapeClose } from '../../shared/hooks/useEscapeClose'
import { useScrimDismiss } from '../../shared/hooks/useScrimDismiss'

/**
 * Visionneuse des IMAGES jointes à une conversation de l'assistant.
 *
 * Pourquoi pas `shared/ui/ImageLightbox` : celle-ci est un PLUGIN reactive-json
 * — elle lit sa collection et l'image ouverte dans le magasin RJ (`useStore`,
 * `propsDataLocationToPathAndValue`) et se ferme par `updateData`. Le panneau
 * d'assistant est du React nu, hors de tout `ReactiveJsonRoot` : il n'a ni
 * magasin ni chemin de données à lui donner, et ses images ne sont pas des
 * fichiers Drupal mais des data URL tenues en mémoire (cf. `attachments.js`).
 *
 * Ce qu'elle fait, et rien de plus : afficher en grand, passer d'une pièce
 * jointe à l'autre du même message, se fermer. Pas de zoom, pas de rail de
 * vignettes — une maquette déposée au chat se regarde, elle ne s'explore pas.
 *
 * Rendue en PORTAIL sur `<body>` avec l'étagement du DS (`modal-scrim-full` +
 * `modal-scrim-over-chrome`) : le panneau d'assistant est en `z-[200]` et le
 * contenu d'un bloc du canevas peut déclarer son propre empilement — une pop-in
 * au `z-50` du DS s'y ferait transpercer.
 *
 * @param {object[]} items    — les pièces jointes OUVRABLES (kind image + viewUrl).
 * @param {string}   openId   — id de celle qui est affichée.
 * @param {Function} onOpen   — demande l'affichage d'un autre id (flèches).
 * @param {Function} onClose  — fermeture.
 */
export function AttachmentViewer({ items, openId, onOpen, onClose }) {
  const index = items.findIndex(a => a.id === openId)
  const current = index >= 0 ? items[index] : null

  const go = useCallback((delta) => {
    if (items.length < 2 || index < 0) return
    onOpen(items[(index + delta + items.length) % items.length].id)
  }, [items, index, onOpen])

  const scrim = useScrimDismiss(onClose)
  useEscapeClose(onClose, Boolean(current))

  // Focus : le dialogue le prend à l'ouverture, le déclencheur le récupère à la
  // fermeture (WCAG 2.4.3). Le dialogue est focusable (`tabIndex={-1}`) pour
  // que les flèches gauche/droite soient servies sans viser un bouton.
  const dialogRef = useRef(null)
  const restoreRef = useRef(null)
  useEffect(() => {
    if (!current) return
    restoreRef.current = document.activeElement
    dialogRef.current?.focus()
    return () => {
      const target = restoreRef.current
      restoreRef.current = null
      if (target && typeof target.focus === 'function' && document.contains(target)) target.focus()
    }
    // Volontairement pas `current` : rouvrir sur une AUTRE image du même message
    // ne doit ni redonner le focus ni changer l'élément à restaurer.
  }, [Boolean(current)]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!current) return null

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1) }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1) }
  }

  return createPortal(
    <div className="modal-scrim modal-scrim-full modal-scrim-over-chrome" {...scrim}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Pièce jointe : ${current.name}`}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="modal-panel modal-panel-full bg-gray-900 focus:outline-none"
      >
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 text-white">
          <span className="truncate text-sm font-medium" title={current.name}>{current.name}</span>
          <div className="flex items-center gap-1">
            {items.length > 1 && (
              <>
                {/* Le rang est DIT, pas seulement dessiné : « 2 / 3 » est la
                    seule indication de position pour qui n'a que le texte. */}
                <span className="mr-1 text-xs text-gray-300" aria-hidden="true">{index + 1} / {items.length}</span>
                <button
                  type="button"
                  onClick={() => go(-1)}
                  aria-label="Pièce jointe précédente"
                  title="Pièce jointe précédente"
                  className="rounded p-1.5 text-gray-200 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  <ChevronLeft size={18} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  aria-label="Pièce jointe suivante"
                  title="Pièce jointe suivante"
                  className="rounded p-1.5 text-gray-200 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer la pièce jointe"
              title="Fermer"
              className="rounded p-1.5 text-gray-200 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 flex items-center justify-center overflow-auto p-3">
          {/* `alt` = le nom du fichier : c'est la seule description dont on
              dispose, et elle vaut mieux qu'une image sans équivalent. */}
          <img
            src={current.viewUrl}
            alt={current.name}
            className="max-h-full max-w-full object-contain"
          />
        </div>
        <p className="px-4 pb-2.5 text-center text-[11px] text-gray-400">
          Copie d'affichage, redimensionnée : le fichier d'origine n'est pas conservé.
        </p>
      </div>
    </div>,
    document.body,
  )
}
